import { defineStore } from 'pinia'
import { inject, ref, computed } from 'vue'
import { getApp } from 'firebase/app'
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { RequestService } from '../services/RequestService'
import { AdminCenterService } from '../services/AdminCenterService'
import { roleKeysForAccountPermission, ROLE_KEYS } from '../access/roleKeys'
import { getAccountOwners } from '../utils/accountOwnersHelpers'

const { handleErrors } = RequestService()

export const useAdminCenterStore = defineStore('adminCenter', () => {
  const functionsBaseUrl = inject('functionsBaseUrl')

  // Instantiate service once during setup so inject() resolves correctly
  const adminCenterService = AdminCenterService()

  // Agency ID resolved directly from Firestore
  const agencyId = ref(null)

  // Unified loading flag for all Admin Center data
  const isLoading = ref(false)

  // Stats
  const totalEmployers = ref(0)
  const unassignedEmployers = ref(0)
  const totalAccountOwners = ref(0)

  // Both lists come back complete from one agency-members call. The tables sort
  // and batch them client-side. allActiveUsers feeds the owner dropdowns.
  const activeUsers = ref([])
  const pendingUsers = ref([])
  const allActiveUsers = computed(() => activeUsers.value)

  const totalActive = ref(0)
  const totalPending = ref(0)

  const invitesSent = computed(() => totalActive.value + totalPending.value)
  const pendingInvites = computed(() => totalPending.value)

  // One agency-employers call returns every plan year. Unassigned is the subset
  // with no owners; the table sorts and batches client-side.
  const allEmployers = ref([])
  const isLoadingEmployers = ref(false)
  const unassignedEmployersList = computed(() => allEmployers.value.filter(employer => !employer.isAssigned))
  const totalAllEmployers = computed(() => allEmployers.value.length)
  const totalUnassignedEmployers = computed(() => unassignedEmployersList.value.length)

  /**
   * Resolves the agency_id for the current authenticated user
   * by querying the 'users' collection directly with the user's UID.
   * This makes the AdminCenter self-contained and independent of HomePage's load order.
   */
  async function resolveAgencyId() {
    if (agencyId.value) return agencyId.value

    const uid = localStorage.getItem('userUid')
    if (!uid) {
      handleErrors('AdminCenterStore.resolveAgencyId', new Error('No userUid in localStorage'))
      return null
    }

    try {
      const db = getFirestore(getApp())
      const userDoc = await getDoc(doc(db, 'users', uid))

      if (!userDoc.exists()) {
        handleErrors('AdminCenterStore.resolveAgencyId', new Error('User document not found'))
        return null
      }

      const userData = userDoc.data()
      agencyId.value = userData.agency_id || null

      if (!agencyId.value) {
        handleErrors('AdminCenterStore.resolveAgencyId', new Error('User has no agency_id'))
      }

      return agencyId.value
    } catch (error) {
      handleErrors('AdminCenterStore.resolveAgencyId', error)
      return null
    }
  }

  async function loadAgencyStats() {
    const currentAgencyId = await resolveAgencyId()
    if (!currentAgencyId) return

    try {
      const db = getFirestore(getApp())

      // Query all employers for this agency
      const employersQuery = query(
        collection(db, 'employers'),
        where('agency_id', '==', String(currentAgencyId))
      )
      const employersSnapshot = await getDocs(employersQuery)

      totalEmployers.value = employersSnapshot.size

      // Count unassigned employers (no account owners at all)
      let unassignedCount = 0
      employersSnapshot.docs.forEach(docSnap => {
        if (getAccountOwners(docSnap.data()).length === 0) {
          unassignedCount++
        }
      })
      unassignedEmployers.value = unassignedCount

    } catch (error) {
      handleErrors('AdminCenterStore.loadAgencyStats', error)
    }
  }

  /**
   * Loads every active and pending member in one call and applies them to state.
   */
  async function _fetchAndApplyUsers() {
    const data = await adminCenterService.getAgencyMembers()
    activeUsers.value = (data.activeUsers || []).map(u => ({ ...u, status: 'Active' }))
    pendingUsers.value = (data.pendingUsers || []).map(u => ({ ...u, status: 'Pending' }))
    totalActive.value = activeUsers.value.length
    totalPending.value = pendingUsers.value.length
    totalAccountOwners.value = data.totalAccountOwners ?? totalActive.value
  }

  /**
   * Loads employer stats and both user lists in parallel.
   * Both cards show loading simultaneously and finish together.
   */
  async function loadAll() {
    isLoading.value = true
    try {
      await Promise.all([loadAgencyStats(), _fetchAndApplyUsers()])
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Creates a single user via the AdminCenterService.
   * @param {Object} formData - { firstName, lastName, title, email, accountPermissions }
   * @returns {Object} result from backend { success, email, status?, error? }
   */
  async function createUser(formData) {
    const currentAgencyId = await resolveAgencyId()
    if (!currentAgencyId) {
      throw new Error('Unable to resolve agency. Please try again.')
    }

    const result = await adminCenterService.createUser({
      firstName: formData.firstName,
      lastName: formData.lastName,
      title: formData.title,
      email: formData.email,
      agencyId: currentAgencyId,
      roleKeys: roleKeysForAccountPermission(formData.accountPermissions)
    })

    await _fetchAndApplyUsers()
    return result
  }

  /**
   * Appends the agencyAdmin role key to a user.
   * @param {string} uid - User ID
   * @returns {Promise<Object>}
   */
  async function grantAgencyAdmin(uid) {
    const result = await adminCenterService.grantUserRoleKey(uid, ROLE_KEYS.AGENCY_ADMIN)
    const user = activeUsers.value.find(u => u.id === uid)
    if (user && !user.roleKeys?.includes(ROLE_KEYS.AGENCY_ADMIN)) {
      user.roleKeys = [...(user.roleKeys || []), ROLE_KEYS.AGENCY_ADMIN]
    }
    return result
  }

  /**
   * Deactivates a user and optionally reassigns their employers.
   * @param {string} uid - User ID
   * @param {string|null} reassignTo - Email of new owner, or null to unassign
   * @returns {Promise<Object>}
   */
  async function deactivateUser(uid, reassignTo = null) {
    const result = await adminCenterService.deactivateUser(uid, reassignTo)
    // Reload both lists to reflect changes
    await _fetchAndApplyUsers()
    return result
  }

  /**
   * Resends the password creation invite email to a pending user.
   * @param {string} uid - User ID of the pending user
   * @returns {Promise<Object>}
   */
  async function resendInvite(uid) {
    const result = await adminCenterService.resendInvite(uid)
    await _fetchAndApplyUsers()
    return result
  }

  /**
   * Cancels a pending invite by deleting the user entirely.
   * @param {string} uid - User ID of the pending user
   * @returns {Promise<Object>}
   */
  async function cancelInvite(uid) {
    const result = await adminCenterService.cancelInvite(uid)
    await _fetchAndApplyUsers()
    return result
  }

  // ── Employer assignments ──

  /**
   * Loads every employer plan year. Unassigned rows are derived from this list.
   */
  async function _fetchAndApplyEmployers() {
    const data = await adminCenterService.getAgencyEmployers({ type: 'all' })
    allEmployers.value = data.employers || []
  }

  async function loadEmployerAssignments() {
    isLoadingEmployers.value = true
    try {
      await _fetchAndApplyEmployers()
    } finally {
      isLoadingEmployers.value = false
    }
  }

  /**
   * Submits batch account-owner changes and reloads users, employers, and stats.
   * @param {Array} assignments - [{ employerId, accountOwners, accountOwner }]
   */
  async function submitAssignments(assignments) {
    const result = await adminCenterService.updateEmployerAssignments(assignments)

    await Promise.all([
      _fetchAndApplyUsers(),
      _fetchAndApplyEmployers(),
      loadAgencyStats()
    ])

    return result
  }

  /**
   * Reloads the full user lists and agency stats.
   */
  async function refreshUsers() {
    isLoading.value = true
    try {
      await Promise.all([
        _fetchAndApplyUsers(),
        loadAgencyStats()
      ])
    } catch (error) {
      handleErrors('AdminCenterStore.refreshUsers', error)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Reloads every employer plan year and agency stats.
   */
  async function refreshEmployers() {
    isLoadingEmployers.value = true
    try {
      await Promise.all([
        _fetchAndApplyEmployers(),
        loadAgencyStats()
      ])
    } catch (error) {
      handleErrors('AdminCenterStore.refreshEmployers', error)
    } finally {
      isLoadingEmployers.value = false
    }
  }

  // ── Transform certification progress ──
  // Loaded once per visit: the gateway returns every learner of the agency unsorted, and
  // TransformCertificationProgress.vue sorts and batches them client-side.
  const certificationProgressRows = ref([])
  const isLoadingCertificationProgress = ref(false)
  const hasLoadedCertificationProgress = ref(false)
  const certificationProgressError = ref('')
  // resetAll (logout) can run while a load is still pending. Each load takes a sequence
  // number and only the latest may touch state, so a slow response for the previous
  // session never repopulates the table for the next one.
  let certificationProgressRequestSeq = 0

  async function loadCertificationProgress() {
    if (isLoadingCertificationProgress.value) return
    certificationProgressRequestSeq += 1
    const requestId = certificationProgressRequestSeq
    const isCurrent = () => requestId === certificationProgressRequestSeq

    isLoadingCertificationProgress.value = true
    certificationProgressError.value = ''
    try {
      const data = await adminCenterService.getTransformCertificationProgress()
      if (!isCurrent()) return
      certificationProgressRows.value = Array.isArray(data?.rows) ? data.rows : []
    } catch (error) {
      if (!isCurrent()) return
      certificationProgressRows.value = []
      // The gateway answers { success, error, message }; axios' own message only carries the
      // status code, which tells the admin nothing.
      certificationProgressError.value =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'Failed to load BCS Transform certification progress.'
    } finally {
      if (isCurrent()) {
        isLoadingCertificationProgress.value = false
        hasLoadedCertificationProgress.value = true
      }
    }
  }

  function resetAll() {
    agencyId.value = null
    isLoading.value = false
    certificationProgressRequestSeq += 1
    certificationProgressRows.value = []
    isLoadingCertificationProgress.value = false
    hasLoadedCertificationProgress.value = false
    certificationProgressError.value = ''
    totalEmployers.value = 0
    unassignedEmployers.value = 0
    totalAccountOwners.value = 0
    activeUsers.value = []
    pendingUsers.value = []
    totalActive.value = 0
    totalPending.value = 0
    allEmployers.value = []
    isLoadingEmployers.value = false
  }

  return {
    agencyId,
    isLoading,
    certificationProgressRows,
    isLoadingCertificationProgress,
    hasLoadedCertificationProgress,
    certificationProgressError,
    loadCertificationProgress,
    totalEmployers,
    unassignedEmployers,
    totalAccountOwners,
    totalActive,
    totalPending,
    invitesSent,
    pendingInvites,
    activeUsers,
    allActiveUsers,
    pendingUsers,
    resolveAgencyId,
    loadAll,
    createUser,
    grantAgencyAdmin,
    deactivateUser,
    resendInvite,
    cancelInvite,
    allEmployers,
    unassignedEmployersList,
    totalAllEmployers,
    totalUnassignedEmployers,
    isLoadingEmployers,
    loadEmployerAssignments,
    submitAssignments,
    refreshUsers,
    refreshEmployers,
    resetAll
  }
})
